import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import debouncePromise from 'debounce-promise'
import { getRegions, getCategories } from 'cityads-v3/actions/offers'
import { getSubscriptionStatuses } from 'cityads-v3/actions/subscriptionStatuses'

/**
 * @deprecated
 */
export function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      const parsedValue = JSON.parse(item);
      return item ? parsedValue : initialValue;
    } catch (error) {
      console.log(error);
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(storedValue));
    } catch(error) {
      console.log(error);
    }
  }, [key, storedValue]);

  return [storedValue, setStoredValue];
}

export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

/**
 * Предзагрузка списка регионов
 */
export function usePreloadRegions() {
  const didMountRef = useRef(null);
  const language = useSelector(state => state.language.language);
  const isLoadedRegions = useSelector(state => state.offers.isLoadedRegions)
  const dispatch = useDispatch();

  /**
   * Подгрузка списка на новом языке
   */
  useEffect(() => {
    if (didMountRef.current) {
      dispatch(getRegions())
    }
  }, [dispatch, language])

  /**
   * Подгрузка списка регионов при первом вызове
   */
  useEffect(() => {
    if (!didMountRef.current && !isLoadedRegions){
      dispatch(getRegions())
    }
    didMountRef.current = true;
  }, [dispatch, isLoadedRegions, language]);

}

/**
 * Подгрузка списка статусов подписок
 */
export function usePreloadSubscriptionStatuses(manualFetch) {
  const didMountRef = useRef(null);
  const isLoaded = useSelector(state => Boolean(state.subscriptionStatuses.isLoaded));
  const language = useSelector(state => state.language.language);
  const dispatch = useDispatch();

  /**
   * Подгрузка списка на новом языке
   */
  useEffect(() => {
    if (didMountRef.current && !manualFetch) {
      dispatch(getSubscriptionStatuses());
    }
  }, [dispatch, language, manualFetch])

  /**
   * Первоначальная загрузка списка статусов подписок
   */
  useEffect(() => {
    if (!didMountRef.current && !isLoaded && !manualFetch) {
      dispatch(getSubscriptionStatuses());
    }

    didMountRef.current = true;
  }, [dispatch, isLoaded, manualFetch]);
}

/**
 * Подгрузка категорий офферов
 */
export function usePreloadCategories(manualFetch) {
  const didMountRef = useRef(null);
  const isLoaded = useSelector(state => state.offers.isLoadedCategories);
  const language = useSelector(state => state.language.language);
  const dispatch = useDispatch();

  /**
   * Подгрузка списка категорий при смене языка
   */
  useEffect(() => {
    if (didMountRef.current && !manualFetch) {
      dispatch(getCategories());
    }
  }, [dispatch, language, manualFetch])

  /**
   * Первоначальная загрузка категорий
   */
  useEffect(() => {
    if (!didMountRef.current && !isLoaded && !manualFetch) {
      dispatch(getCategories());
    }

    didMountRef.current = true;
  }, [dispatch, isLoaded, manualFetch]);
}

/**
 * Выполнение только последнего действия за промежуток времени.
 *
 * const act = useDebounceAction(() => console.log('action'), 500);
 * act();
 * act();
 * act(); // action
 */
export function useDebounceAction(callback, timeout) {
  const timerId = useRef(null)

  const act = useCallback((data) => {
    clearTimeout(timerId.current)
    timerId.current = setTimeout(() => {
      callback(data)
    }, timeout)
  }, [callback, timeout])

  useEffect(() => {
    return () => clearTimeout(timerId.current)
  }, [])

  return act
}

export function useDebouncePromise(callback, timeout, options = undefined) {
  return useMemo(() => {
    return debouncePromise(callback, timeout, options)
  }, [callback, timeout, options])
}

/**
 * Кеширование ранее загруженных данных на время, пока загружаются новые
 */
export function useDebounceData(isWaiting, data) {
  const [cachedData, setCachedData] = useState(data);

  useEffect(() => {
    if (!isWaiting) {
      setCachedData(data);
    }
  }, [isWaiting, data]);

  return isWaiting ? cachedData : data;
}
